import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, logout as apiLogout, restoreSession, setUnauthenticatedHandler } from '@/lib/api';
import type { UserIdentity } from '@/types/api';

/* The signed-in expert. Deliberately NOT a Zustand store: the house rule is
   that Query owns anything with a URL and Zustand owns anything with a socket,
   and a session is the first of those — `/expert/me` is a URL. The access token
   itself lives in module memory inside lib/api.ts, never in a store and never in
   localStorage, so a script that reaches this app's memory gets a token that
   expires in minutes rather than a refresh family.

   That matters more here than on the operator console. This dashboard renders
   markdown an agent produced after reading an untrusted repository, and an
   expert's token can claim chats, prompt agents and merge to production. */

type Status = 'restoring' | 'authenticated' | 'anonymous';

interface SessionValue {
  status: Status;
  expert: UserIdentity | null;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
}

const SessionContext = React.createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<Status>('restoring');
  const [expert, setExpert] = React.useState<UserIdentity | null>(null);
  const queryClient = useQueryClient();

  const loadExpert = React.useCallback(async () => {
    // The role guard is server-side and authoritative. This call is how the
    // shell learns the display name, and how a revoked account is noticed
    // promptly rather than at the next action.
    const me = await api.get<UserIdentity>('/expert/me');
    setExpert(me);
    setStatus('authenticated');
  }, []);

  // A cold page load has no access token but may still hold the HttpOnly
  // refresh cookie. Rotate once; if that fails the expert signs in.
  React.useEffect(() => {
    let cancelled = false;

    void (async () => {
      const restored = await restoreSession();
      if (cancelled) return;
      if (!restored) {
        setStatus('anonymous');
        return;
      }
      try {
        await loadExpert();
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadExpert]);

  // The API layer calls this when the refresh family is gone. Reuse of a
  // rotated refresh token revokes the whole family by design, so this is a
  // sign-in-again signal and never a retry.
  React.useEffect(() => {
    setUnauthenticatedHandler(() => {
      setExpert(null);
      setStatus('anonymous');
      queryClient.clear();
    });
    return () => setUnauthenticatedHandler(null);
  }, [queryClient]);

  const signOut = React.useCallback(async () => {
    await apiLogout();
    setExpert(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  const value = React.useMemo<SessionValue>(
    () => ({ status, expert, signOut, reload: loadExpert }),
    [status, expert, signOut, loadExpert],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = React.useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside SessionProvider');
  return value;
}
